import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type MaintenanceReason = "admin" | "outage" | null;

export interface MaintenanceState {
  active: boolean;
  reason: MaintenanceReason;
  expectedDate?: string;
  ceoName?: string;
  message?: string;
  checking: boolean;
  retry: () => void;
}

const POLL_MS = 20_000;
const TIMEOUT_MS = 8_000;
const FAIL_THRESHOLD = 2;

function withTimeout<T>(p: PromiseLike<T>): Promise<T | null> {
  return Promise.race([
    Promise.resolve(p).catch(() => null),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), TIMEOUT_MS)),
  ]);
}

/**
 * Modo de manutenção BeraStays.
 * - Manual: platform_settings -> platform_maintenance.site_enabled === true
 * - Automático: backend indisponível (serviços pausados) em 2 verificações seguidas.
 * Volta ao normal sozinho assim que uma verificação tem sucesso / o super admin desliga.
 */
export function useMaintenanceMode(): MaintenanceState {
  const [adminActive, setAdminActive] = useState(false);
  const [outage, setOutage] = useState(false);
  const [checking, setChecking] = useState(false);
  const [meta, setMeta] = useState<{ expected_date?: string; ceo_name?: string; message?: string }>({});
  const failures = useRef(0);
  const mounted = useRef(true);

  const check = useCallback(async () => {
    setChecking(true);
    const res = await withTimeout(
      supabase
        .from("platform_settings")
        .select("value")
        .eq("key", "platform_maintenance")
        .maybeSingle()
    );
    if (!mounted.current) return;
    setChecking(false);

    const reachable = !!res && !(res as any).error;
    if (!reachable) {
      failures.current += 1;
      if (failures.current >= FAIL_THRESHOLD) setOutage(true);
      return;
    }

    failures.current = 0;
    setOutage(false);

    const value = (res as any)?.data?.value;
    if (value && typeof value === "object") {
      setAdminActive(value.site_enabled === true || value.site_enabled === "true");
      setMeta({
        expected_date: value.expected_date,
        ceo_name: value.ceo_name,
        message: value.message,
      });
    } else {
      setAdminActive(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    check();
    const id = window.setInterval(check, POLL_MS);

    const channel = supabase
      .channel("platform-maintenance")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "platform_settings" },
        () => check()
      )
      .subscribe();

    const onOnline = () => check();
    window.addEventListener("online", onOnline);

    return () => {
      mounted.current = false;
      window.clearInterval(id);
      window.removeEventListener("online", onOnline);
      supabase.removeChannel(channel);
    };
  }, [check]);

  return {
    active: adminActive || outage,
    reason: adminActive ? "admin" : outage ? "outage" : null,
    expectedDate: meta.expected_date,
    ceoName: meta.ceo_name,
    message: meta.message,
    checking,
    retry: check,
  };
}
